import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createTeamInvitationRequest, fetchTeamInvitations } from "../lib/api-client";
import type { TeamInvitation } from "../types/api";

function TeamInvitationsPage(): JSX.Element {
  const { teamId } = useParams<{ teamId: string }>();
  const queryClient = useQueryClient();

  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("FormEditor");
  const [inviteExpiry, setInviteExpiry] = useState(7);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const invitationsQuery = useQuery({
    queryKey: ["team-invitations", teamId],
    queryFn: () => fetchTeamInvitations(teamId!),
    enabled: Boolean(teamId),
  });

  const inviteMutation = useMutation({
    mutationFn: async () => {
      if (!teamId) throw new Error("teamId missing");
      if (!inviteEmail) throw new Error("Email is required");
      return createTeamInvitationRequest(teamId, {
        email: inviteEmail,
        role: inviteRole,
        expiresInDays: inviteExpiry,
      });
    },
    onSuccess() {
      setInviteEmail("");
      setShowInviteDialog(false);
      void queryClient.invalidateQueries({ queryKey: ["team-invitations", teamId] });
      setToast({ type: "success", message: "Invitation sent successfully." });
      // clear toast after 3s
      setTimeout(() => setToast(null), 3000);
    },
    onError(error: unknown) {
      setToast({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to send invitation.",
      });
    },
  });

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "var(--color-bg)" }}>
      <header
        style={{
          padding: "1.5rem 2rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid var(--color-border)",
          background: "var(--color-surface)",
        }}
      >
        <div>
          <h1 style={{ fontSize: "1.5rem", margin: 0, fontWeight: 600 }}>Invitations</h1>
          <p style={{ margin: "0.25rem 0 0 0", color: "var(--color-text-muted)" }}>
            Manage pending team invitations.
          </p>
        </div>
        <button
          type="button"
          className="button primary"
          onClick={() => setShowInviteDialog(true)}
        >
          Invite Member
        </button>
      </header>

      <main style={{ padding: "2rem", flex: 1, maxWidth: "1200px", margin: "0 auto", width: "100%" }}>
        <div className="surface" style={{ padding: "1.5rem", borderRadius: "8px", boxShadow: "var(--shadow-sm)" }}>
          <InvitationsTable
            invitations={invitationsQuery.data?.invitations ?? []}
            isLoading={invitationsQuery.isLoading}
          />
        </div>
      </main>

      {/* Invite Member Dialog */}
      {showInviteDialog && (
        <Dialog
          title="Invite New Member"
          onClose={() => setShowInviteDialog(false)}
          onSubmit={() => inviteMutation.mutateAsync()}
          isLoading={inviteMutation.isPending}
          submitLabel="Send Invitation"
        >
          <div className="form-group" style={{ display: "grid", gap: "1rem" }}>
            <label className="field">
              <span>Email Address</span>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@example.com"
                required
                className="input"
                autoFocus
              />
            </label>
            <label className="field">
              <span>Role</span>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="select"
              >
                <option value="TeamAdmin">Team Admin</option>
                <option value="FormEditor">Form Editor</option>
                <option value="FormViewer">Form Viewer</option>
                <option value="AnalyticsViewer">Analytics Viewer</option>
                <option value="DomainManager">Domain Manager</option>
              </select>
            </label>
            <label className="field">
              <span>Expires in (days)</span>
              <input
                type="number"
                min={1}
                max={30}
                value={inviteExpiry}
                onChange={(e) => setInviteExpiry(Number(e.target.value))}
                className="input"
              />
              <span className="help-text" style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
                Invitation link will expire after this many days.
              </span>
            </label>
          </div>
        </Dialog>
      )}

      {/* Toast Notification */}
      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: "2rem",
            right: "2rem",
            background: toast.type === "success" ? "#10B981" : "#EF4444",
            color: "white",
            padding: "0.8rem 1.5rem",
            borderRadius: "6px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            zIndex: 2000,
            fontWeight: 500,
            animation: "slideIn 0.3s ease-out",
          }}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

function InvitationsTable({
  invitations,
  isLoading,
}: {
  invitations: TeamInvitation[];
  isLoading: boolean;
}): JSX.Element {
  if (isLoading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)" }}>
        Loading invitations...
      </div>
    );
  }
  if (invitations.length === 0) {
    return (
      <div style={{ padding: "3rem 2rem", textAlign: "center", color: "var(--color-text-muted)" }}>
        <div style={{ fontSize: "1.2rem", marginBottom: "0.5rem" }}>No pending invitations</div>
        <p>Invite team members to collaborate on your forms.</p>
      </div>
    );
  }
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
      <thead>
        <tr style={{ borderBottom: "1px solid var(--color-border)", textAlign: "left", color: "var(--color-text-muted)" }}>
          <th style={{ padding: "0.75rem 1rem", fontWeight: 500 }}>Email</th>
          <th style={{ padding: "0.75rem 1rem", fontWeight: 500 }}>Role</th>
          <th style={{ padding: "0.75rem 1rem", fontWeight: 500 }}>Status</th>
          <th style={{ padding: "0.75rem 1rem", fontWeight: 500 }}>Expires</th>
          {/* Action column placeholder for future revocation feature */}
          {/* <th style={{ padding: "0.75rem 1rem", fontWeight: 500, textAlign: "right" }}>Actions</th> */}
        </tr>
      </thead>
      <tbody>
        {invitations.map((invitation) => (
          <tr key={invitation.id} style={{ borderBottom: "1px solid var(--color-border-light)", transition: "background 0.2s" }}>
            <td style={{ padding: "0.75rem 1rem", fontWeight: 500 }}>{invitation.email}</td>
            <td style={{ padding: "0.75rem 1rem" }}>
              <RoleBadge role={invitation.role} />
            </td>
            <td style={{ padding: "0.75rem 1rem" }}>
              <StatusBadge status={invitation.status} />
            </td>
            <td style={{ padding: "0.75rem 1rem", color: "var(--color-text-muted)" }}>
              {new Date(invitation.expiresAt).toLocaleDateString()}
            </td>
            {/* <td style={{ padding: "0.75rem 1rem", textAlign: "right" }}>...</td> */}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RoleBadge({ role }: { role: string }) {
  // Simple styling for roles
  return (
    <span
      style={{
        background: "var(--color-bg-soft)",
        padding: "2px 8px",
        borderRadius: "4px",
        fontSize: "0.85rem",
        color: "var(--color-text)",
        border: "1px solid var(--color-border)",
      }}
    >
      {role}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; color: string }> = {
    pending: { bg: "#FEF3C7", color: "#92400E" }, // Yellow/Orange
    accepted: { bg: "#D1FAE5", color: "#065F46" }, // Green
    expired: { bg: "#F3F4F6", color: "#6B7280" }, // Gray
    declined: { bg: "#FEE2E2", color: "#991B1B" }, // Red
  };

  const style = styles[status] || styles.pending;

  return (
    <span
      style={{
        background: style.bg,
        color: style.color,
        padding: "2px 8px",
        borderRadius: "12px",
        fontSize: "0.75rem",
        fontWeight: 600,
        textTransform: "capitalize",
      }}
    >
      {status}
    </span>
  );
}

function Dialog({
  title,
  onClose,
  onSubmit,
  isLoading,
  children,
  submitLabel = "Create",
  width = "450px",
}: {
  title: string;
  onClose: () => void;
  onSubmit: () => void;
  isLoading: boolean;
  children: React.ReactNode;
  submitLabel?: string;
  width?: string;
}): JSX.Element {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        backdropFilter: "blur(2px)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "var(--color-surface)",
          borderRadius: "8px",
          padding: "0",
          width: width,
          maxWidth: "95vw",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
          animation: "scaleIn 0.2s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: "1.5rem", borderBottom: "1px solid var(--color-border)" }}>
          <h2 style={{ fontSize: "1.25rem", fontWeight: 600, margin: 0 }}>{title}</h2>
        </div>

        <div style={{ padding: "1.5rem", overflowY: "auto" }}>
          <form
            id="dialog-form"
            onSubmit={(e) => {
              e.preventDefault();
              void onSubmit();
            }}
          >
            {children}
          </form>
        </div>

        <div style={{ padding: "1rem 1.5rem", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end", gap: "0.75rem", background: "var(--color-bg-soft)", borderBottomLeftRadius: "8px", borderBottomRightRadius: "8px" }}>
          <button type="button" className="button ghost" onClick={onClose} disabled={isLoading}>
            Cancel
          </button>
          <button type="submit" form="dialog-form" className="button primary" disabled={isLoading}>
            {isLoading ? "Sending..." : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TeamInvitationsPage;
